import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "@/context/AppContext";
import ProductCard from "@/components/ProductCard";
import { Award, Leaf, Truck, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";

export default function Home() {
  const { t } = useApp();
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then(r => setFeatured(r.data));
    axios.get(`${API}/products?bestseller=true`).then(r => setBestsellers(r.data));
    document.title = t("page_title");
  }, [t]);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden">
        <picture>
          <source type="image/webp" media="(max-width: 640px)" srcSet="/hero/hero-400.webp 1x, /hero/hero-800.webp 2x" />
          <source type="image/webp" media="(max-width: 1024px)" srcSet="/hero/hero-800.webp 1x, /hero/hero-1600.webp 2x" />
          <source type="image/webp" srcSet="/hero/hero-800.webp 800w, /hero/hero-1600.webp 1600w" sizes="100vw" />
          <img src="/hero/hero-800.webp" alt={t("home_studio_alt")} width="1600" height="2000" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40"></div>
        <div className="container-narrow relative pb-20 pt-32">
          <div className="max-w-3xl">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">Coiffeur Tattoo 01&Co. — Glarus</div>
            <h1 className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-title">{t("hero_title")}</h1>
            <p className="text-white/70 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">{t("hero_subtitle")}</p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/shop" className="btn-gold" data-testid="hero-cta">{t("hero_cta")} <ArrowRight size={14} /></Link>
              <Link to="/about" className="btn-outline" data-testid="hero-secondary">{t("hero_secondary")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="container-narrow py-24">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <div className="eyebrow mb-3">01</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white">{t("section_featured")}</h2>
          </div>
          <Link to="/shop" className="text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-gold link-underline">{t("view_all")}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {featured.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Why us */}
      <section className="bg-[#121212] py-24 border-y border-white/5">
        <div className="container-narrow">
          <div className="eyebrow mb-3">02</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white mb-14 max-w-2xl">{t("section_why_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: Award, key: "reason_swiss" },
              { icon: Leaf, key: "reason_natural" },
              { icon: Truck, key: "reason_shipping" },
              { icon: ShieldCheck, key: "reason_secure" },
            ].map(({icon: Icon, key}) => (
              <div key={key} className="border-t border-gold/40 pt-6" data-testid={`reason-${key}`}>
                <Icon size={24} className="text-gold mb-4" strokeWidth={1.25} />
                <h3 className="text-white text-lg mb-2 font-serif-display">{t(key)}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{t(key + "_desc")}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="container-narrow py-24">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <div className="eyebrow mb-3">03</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white">{t("section_bestsellers")}</h2>
          </div>
          <Link to="/shop" className="text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-gold link-underline">{t("view_all")}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {bestsellers.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Swiss story */}
      <section className="relative py-32 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1745318120215-23be5dd5cf47?auto=format&fit=crop&w=1200&q=70" alt="" loading="lazy" decoding="async" width="1200" height="800" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="container-narrow relative">
          <div className="max-w-2xl">
            <div className="eyebrow mb-4">{t("story_since_2011")}</div>
            <h2 className="font-serif-display text-white text-4xl sm:text-5xl leading-tight mb-6">{t("story_title")}</h2>
            <p className="text-white/70 leading-relaxed mb-8">{t("story_body")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/about" className="btn-outline" data-testid="story-cta">{t("hero_secondary")} <ArrowRight size={14} /></Link>
              <Link to="/consultation" className="btn-outline"><MessageCircle size={14} /> {t("nav_consultation")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="container-narrow py-24">
        <div className="border border-white/10 p-10 md:p-16 bg-[#121212]">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="eyebrow mb-3">{t("newsletter_label")}</div>
              <h3 className="font-serif-display text-3xl sm:text-4xl text-white mb-3">{t("newsletter_title")}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t("newsletter_desc")}</p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => { e.preventDefault(); }}>
              <input data-testid="newsletter-email" type="email" placeholder={t("newsletter_placeholder")} required />
              <button className="btn-gold shrink-0" data-testid="newsletter-submit">{t("newsletter_submit")}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
